/**
 * The annotation outbox: ONE FIFO per device, every entry tagged with the user who made it.
 *
 *  - Collapsing: a new op on the same key (object id, or layer id for layer ops) replaces the
 *    payload but keeps the EARLIEST queue position (seq), so a layer created offline always travels
 *    ahead of the annotations drawn on it.
 *  - Every collapse bumps `rev`; the flusher only removes an entry when the rev it sent is still
 *    the current one (an edit made while a request was in flight is sent again).
 *  - Pure in-memory model: the engine persists the records it returns, keyed by `key` (several
 *    tabs share the stored queue). `seq` only orders the queue: max(last + 1, now), so ops created
 *    in different tabs keep their real order; ties are broken by key.
 */
import { SYNC_LIMITS, type AnnotationRow, type UpsertLayerRequest } from '../../../shared/dto/annotations';

/**
 * POST /layers body. `keepName` marks a pure sheet-revision confirmation ("Staðfesta"): the server
 * keeps the layer's current name (a rename made on another device is not reverted). It is dropped
 * as soon as a create or rename collapses with it, so the name then travels as usual.
 */
export type LayerUpsertPayload = UpsertLayerRequest & { keepName?: true };

export type OpBody =
  | { type: 'ann'; payload: AnnotationRow }
  | { type: 'layer.upsert'; payload: LayerUpsertPayload }
  | { type: 'layer.rename'; payload: { name: string } }
  | { type: 'layer.delete'; payload: null };

export type OpType = OpBody['type'];

export interface OpMeta {
  userId: string;
  sheetFileId: string;
  layerId: string;
}

export type OpInput = OpBody & OpMeta;

export interface OpFailure {
  status: number;
  code: string;
  at: number;
}

export type OpRecord = OpInput & {
  /** Queue position (FIFO). Stable for the lifetime of the key. */
  seq: number;
  key: string;
  /** Bumped on every collapse. */
  rev: number;
  attempts: number;
  /** Non-retryable rejection (4xx). Failed ops are skipped by automatic flushes. */
  failed: OpFailure | null;
  createdAt: number;
  updatedAt: number;
};

export const annKey = (objectId: string) => `a:${objectId}`;
export const layerKey = (layerId: string) => `l:${layerId}`;

export function opKey(op: OpInput): string {
  return op.type === 'ann' ? annKey(op.payload.id) : layerKey(op.layerId);
}

const bodyOf = (o: OpBody): OpBody => ({ type: o.type, payload: o.payload }) as OpBody;

/** The upsert without the keep-name marker (it now carries a name the server must take). */
const withName = (p: LayerUpsertPayload, name = p.name): LayerUpsertPayload => ({ id: p.id, sheetFileId: p.sheetFileId, name, sheetRev: p.sheetRev });

/** Merges a new op body into a queued one for the same key (returns only { type, payload }). */
export function collapseBody(prev: OpBody, next: OpBody): OpBody {
  if (prev.type === 'ann' || next.type === 'ann') return bodyOf(next);
  switch (next.type) {
    case 'layer.delete':
      return bodyOf(next);
    case 'layer.upsert':
      // A revision confirmation on top of a queued create/rename: send the name too (the
      // confirmation carries the current local name, i.e. the renamed one).
      if (next.payload.keepName && (prev.type === 'layer.rename' || (prev.type === 'layer.upsert' && !prev.payload.keepName))) {
        return { type: 'layer.upsert', payload: withName(next.payload) };
      }
      return bodyOf(next);
    case 'layer.rename':
      // Not yet created on the server (or a queued revision confirmation): one upsert with the new name.
      if (prev.type === 'layer.upsert') return { type: 'layer.upsert', payload: withName(prev.payload, next.payload.name) };
      // A delete is final.
      if (prev.type === 'layer.delete') return bodyOf(prev);
      return bodyOf(next);
  }
}

export interface OutboxStats {
  total: number;
  failed: number;
  bySheet: ReadonlyMap<string, { pending: number; failed: number }>;
  byLayer: ReadonlyMap<string, number>;
}

const EMPTY_STATS: OutboxStats = { total: 0, failed: 0, bySheet: new Map(), byLayer: new Map() };

export class Outbox {
  private byKey = new Map<string, OpRecord>();
  private lastSeq = 0;
  private sorted: OpRecord[] | null = null;
  private statsCache: { userId: string; version: number; stats: OutboxStats } | null = null;
  /** Bumped on every change. */
  version = 0;

  /**
   * Rebuilds the queue from persisted records. Duplicate keys (only possible in the version-1
   * store, which was keyed by seq) keep the earliest position with the newest payload: `obsolete`
   * are the superseded records and `rewrite` the merged ones to store again.
   */
  static restore(records: readonly OpRecord[]): { outbox: Outbox; obsolete: OpRecord[]; rewrite: OpRecord[] } {
    const box = new Outbox();
    const obsolete: OpRecord[] = [];
    const rewritten = new Set<string>();
    const bySeq = [...records].sort((a, b) => a.seq - b.seq);
    for (const r of bySeq) {
      box.lastSeq = Math.max(box.lastSeq, r.seq);
      const prev = box.byKey.get(r.key);
      if (prev) {
        obsolete.push(r);
        rewritten.add(r.key);
        box.byKey.set(r.key, {
          ...prev,
          ...collapseBody(prev, r),
          rev: Math.max(prev.rev, r.rev) + 1,
          failed: r.failed,
          updatedAt: Math.max(prev.updatedAt, r.updatedAt),
        } as OpRecord);
      } else {
        box.byKey.set(r.key, r);
      }
    }
    const rewrite = [...rewritten].map((k) => box.byKey.get(k)!);
    return { outbox: box, obsolete, rewrite };
  }

  get size(): number {
    return this.byKey.size;
  }

  get(key: string): OpRecord | undefined {
    return this.byKey.get(key);
  }

  has(key: string): boolean {
    return this.byKey.has(key);
  }

  /** Adds an op, or collapses it into the queued op with the same key. Returns the record to persist. */
  enqueue(input: OpInput, now: number): OpRecord {
    const key = opKey(input);
    const prev = this.byKey.get(key);
    let rec: OpRecord;
    if (prev) {
      rec = {
        ...prev,
        ...collapseBody(prev, input),
        userId: input.userId,
        sheetFileId: input.sheetFileId,
        layerId: input.layerId,
        rev: prev.rev + 1,
        attempts: 0,
        failed: null,
        updatedAt: now,
      } as OpRecord;
    } else {
      this.lastSeq = Math.max(this.lastSeq + 1, Number.isFinite(now) ? Math.floor(now) : 0);
      rec = { ...input, key, seq: this.lastSeq, rev: 1, attempts: 0, failed: null, createdAt: now, updatedAt: now } as OpRecord;
    }
    this.byKey.set(key, rec);
    this.touch();
    return rec;
  }

  /**
   * Adds stored records whose key this queue doesn't hold (ops queued by another tab, or left by
   * one that was closed). Keys already held keep the in-memory version: it is either the same
   * or newer than what this tab stored, and a different version stored by another tab stays in
   * storage (see sameOpVersion) and is picked up after ours is sent. Returns how many were added.
   */
  absorb(records: readonly OpRecord[]): number {
    let n = 0;
    for (const r of records) {
      if (!r || typeof r.key !== 'string' || this.byKey.has(r.key)) continue;
      this.byKey.set(r.key, r);
      if (Number.isFinite(r.seq)) this.lastSeq = Math.max(this.lastSeq, r.seq);
      n++;
    }
    if (n) this.touch();
    return n;
  }

  /** Removes an op (only when its rev still equals `rev`, if given). Returns the removed record. */
  remove(key: string, rev?: number): OpRecord | null {
    const cur = this.byKey.get(key);
    if (!cur || (rev !== undefined && cur.rev !== rev)) return null;
    this.byKey.delete(key);
    this.touch();
    return cur;
  }

  removeWhere(pred: (op: OpRecord) => boolean): OpRecord[] {
    const out: OpRecord[] = [];
    for (const [k, op] of this.byKey) {
      if (pred(op)) {
        this.byKey.delete(k);
        out.push(op);
      }
    }
    if (out.length) this.touch();
    return out;
  }

  /** Marks the op as rejected (when `rev` is still current). Returns the updated record. */
  markFailed(key: string, rev: number, failure: OpFailure): OpRecord | null {
    const cur = this.byKey.get(key);
    if (!cur || cur.rev !== rev) return null;
    const rec: OpRecord = { ...cur, failed: failure, attempts: cur.attempts + 1 };
    this.byKey.set(key, rec);
    this.touch();
    return rec;
  }

  /** Makes this user's failed ops eligible again (manual retry). Returns the changed records. */
  clearFailed(userId: string): OpRecord[] {
    const out: OpRecord[] = [];
    for (const [k, op] of this.byKey) {
      if (op.userId === userId && op.failed) {
        const rec: OpRecord = { ...op, failed: null };
        this.byKey.set(k, rec);
        out.push(rec);
      }
    }
    if (out.length) this.touch();
    return out;
  }

  /** All ops in queue order. */
  ordered(): readonly OpRecord[] {
    if (!this.sorted) this.sorted = [...this.byKey.values()].sort((a, b) => a.seq - b.seq || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return this.sorted;
  }

  countFor(userId: string): number {
    let n = 0;
    for (const op of this.byKey.values()) if (op.userId === userId) n++;
    return n;
  }

  /** Pending/failed counts of one user's ops (memoised per version). */
  stats(userId: string): OutboxStats {
    const c = this.statsCache;
    if (c && c.userId === userId && c.version === this.version) return c.stats;
    let stats = EMPTY_STATS;
    if (this.byKey.size) {
      let total = 0;
      let failed = 0;
      const bySheet = new Map<string, { pending: number; failed: number }>();
      const byLayer = new Map<string, number>();
      for (const op of this.byKey.values()) {
        if (op.userId !== userId) continue;
        total++;
        const s = bySheet.get(op.sheetFileId) ?? { pending: 0, failed: 0 };
        s.pending++;
        if (op.failed) {
          failed++;
          s.failed++;
        }
        bySheet.set(op.sheetFileId, s);
        byLayer.set(op.layerId, (byLayer.get(op.layerId) ?? 0) + 1);
      }
      stats = { total, failed, bySheet, byLayer };
    }
    this.statsCache = { userId, version: this.version, stats };
    return stats;
  }

  private touch() {
    this.sorted = null;
    this.version++;
  }
}

/**
 * True when two records of the same key carry the same version of the op (failure state and
 * attempt counters aside). Stored ops are only deleted when they still hold the version that was
 * sent, so a newer version written by another tab survives.
 */
export function sameOpVersion(a: OpRecord, b: OpRecord): boolean {
  if (a.key !== b.key || a.type !== b.type || a.updatedAt !== b.updatedAt) return false;
  if (a.type === 'ann' && b.type === 'ann') return a.payload.hlcMs === b.payload.hlcMs && a.payload.deviceId === b.payload.deviceId;
  return JSON.stringify(a.payload) === JSON.stringify(b.payload);
}

// ---------------------------------------------------------------------------------------------
// Flush planning

/** Server limits are 50 ops / 64 KB per push (413 above): stay at 60 KB to leave a margin. */
export const PUSH_MAX_OPS = SYNC_LIMITS.pushOps;
export const PUSH_MAX_BYTES = SYNC_LIMITS.pushBytes - 4 * 1024;
const ENVELOPE_BYTES = '{"ops":[]}'.length;

export interface PushLimits {
  maxOps: number;
  maxBytes: number;
}
export const DEFAULT_PUSH_LIMITS: PushLimits = { maxOps: PUSH_MAX_OPS, maxBytes: PUSH_MAX_BYTES };

/** UTF-8 byte length without allocating. */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        n += 4;
        i++;
      } else n += 3;
    } else n += 3;
  }
  return n;
}

export const rowBytes = (row: AnnotationRow): number => utf8Length(JSON.stringify(row));

export type FlushStep = { kind: 'push'; ops: OpRecord[] } | { kind: 'layer'; op: OpRecord };

/**
 * The next request to send: either one layer op, or the longest run of consecutive eligible
 * annotation ops from the head of the queue within the count/byte limits. A single op larger than
 * the byte limit still goes alone (the server decides). Ineligible ops are skipped, not barriers.
 */
export function nextStep(
  ordered: readonly OpRecord[],
  eligible: (op: OpRecord) => boolean,
  limits: PushLimits = DEFAULT_PUSH_LIMITS,
  sizeOf: (row: AnnotationRow) => number = rowBytes,
): FlushStep | null {
  const ops: OpRecord[] = [];
  let bytes = ENVELOPE_BYTES;
  for (const op of ordered) {
    if (!eligible(op)) continue;
    if (op.type !== 'ann') {
      if (ops.length) break;
      return { kind: 'layer', op };
    }
    const size = sizeOf(op.payload) + (ops.length ? 1 : 0);
    if (ops.length && (ops.length >= limits.maxOps || bytes + size > limits.maxBytes)) break;
    ops.push(op);
    bytes += size;
  }
  return ops.length ? { kind: 'push', ops } : null;
}

/** All steps for a queue snapshot (as if every step succeeded). Used by tests and diagnostics. */
export function planSteps(
  ordered: readonly OpRecord[],
  eligible: (op: OpRecord) => boolean,
  limits: PushLimits = DEFAULT_PUSH_LIMITS,
  sizeOf: (row: AnnotationRow) => number = rowBytes,
): FlushStep[] {
  const taken = new Set<string>();
  const steps: FlushStep[] = [];
  for (;;) {
    const step = nextStep(ordered, (op) => !taken.has(op.key) && eligible(op), limits, sizeOf);
    if (!step) return steps;
    steps.push(step);
    if (step.kind === 'layer') taken.add(step.op.key);
    else for (const op of step.ops) taken.add(op.key);
  }
}

// ---------------------------------------------------------------------------------------------
// Failure handling

/** 'retry' = stop and try again later · 'auth' = stop, keep queue until re-login · 'fail' = mark op failed, continue. */
export type FailureClass = 'retry' | 'auth' | 'fail';

export function classifyStatus(status: number): FailureClass {
  if (status === 401) return 'auth';
  if (status === 408 || status === 425 || status === 429 || status >= 500) return 'retry';
  return 'fail';
}

export const MAX_BACKOFF_MS = 5 * 60_000;

/** Exponential backoff with ±25 % jitter: 2 s, 4 s, 8 s … capped at 5 min. */
export function backoffMs(failures: number, rand: () => number = Math.random): number {
  const base = Math.min(MAX_BACKOFF_MS, 2000 * 2 ** Math.max(0, Math.min(20, failures - 1)));
  return Math.round(base * (0.75 + rand() * 0.5));
}
